import { useMemo, useState } from 'react';
import { Chip, ListItemButton } from '@mui/material';
import { DailyNavReportColumnMapping, FirmDailyNavConfigVersion } from 'api';
import {
  DAILY_NAV_DATA_CUTOFF_LABEL,
  DAILY_NAV_WINDOW_SESSIONS_FIELDS,
  dailyNavDataCutoffLabel,
  dailyNavDateLabel,
  dailyNavMarketTimezone,
  dailyNavNextDayLabel,
  formatDailyNavEffectivePeriod,
  formatDailyNavReportSendTime,
  fractionToPercent,
  getDailyNavThresholdGroups,
  monitoringModeLabel,
  tradingMarketLabel,
} from 'common/constants/dailyNav';
import { Box, Button, Divider, List, ListItem, ListItemText, Stack, Text } from 'components/ui';
import { PrismThemeProvider } from 'theme';
import { DailyNavFirmVersionHistoryProps } from './DailyNavFirmVersionHistory.props';

const recipientsLabel = (recipients: unknown) =>
  Array.isArray(recipients) && recipients.length ? (recipients as string[]).join(', ') : 'None';

// Firm config versions with in-effect/next-day chips and a values-in-effect detail.
// Shared by Daily NAV Settings and the setup wizard's context panel.
const DailyNavFirmVersionHistory = ({
  history,
  hasMoreHistory,
  isLoadingMoreHistory,
  loadMoreHistory,
  anchoredVersionId,
  openVersionId,
  hasDivergence,
  governingNavDate,
  nextNavDate,
  renderActiveVersionAction,
}: DailyNavFirmVersionHistoryProps) => {
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);
  const selectedVersion = useMemo<FirmDailyNavConfigVersion | null>(
    () => history?.find(version => version.id === selectedVersionId) ?? null,
    [history, selectedVersionId]
  );

  // Rendered on pages without the Prism theme, which components/ui needs.
  return (
    <PrismThemeProvider>
      <Text variant="h4" gutterBottom>
        Version History
      </Text>
      {history?.length ? (
        <List dense>
          {history.map(version => (
            /* secondaryAction keeps the action out of ListItemButton so the trailing
                 control isn't nested inside the row's own interactive element. */
            <ListItem
              key={version.id}
              disablePadding
              secondaryAction={version.is_active ? renderActiveVersionAction?.(version) : undefined}>
              <ListItemButton
                selected={version.id === selectedVersionId}
                data-testid={`daily-nav-firm-version-${version.id}`}
                onClick={() => setSelectedVersionId(version.id === selectedVersionId ? null : (version.id ?? null))}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <span>{formatDailyNavEffectivePeriod(version)}</span>
                      {/* Two facts, not one: after a mid-day save a lone "Current"
                          chip marks the version NOT driving today's numbers.
                          Collapses to one chip when they agree. */}
                      {version.id === anchoredVersionId && (
                        <Chip
                          size="small"
                          color={hasDivergence ? 'warning' : 'success'}
                          label={
                            hasDivergence
                              ? `In effect for ${dailyNavDateLabel(governingNavDate)} NAV`
                              : 'Current — today’s NAV and the next'
                          }
                          data-testid={`daily-nav-firm-version-in-effect-${version.id}`}
                        />
                      )}
                      {hasDivergence && version.id === openVersionId && (
                        <Chip
                          size="small"
                          color="info"
                          label={dailyNavNextDayLabel(nextNavDate)}
                          data-testid={`daily-nav-firm-version-next-${version.id}`}
                        />
                      )}
                      {/* No open NAV day to compare against; still mark the open one. */}
                      {anchoredVersionId === null && version.is_active && (
                        <Chip
                          size="small"
                          color="success"
                          label="Current"
                          data-testid={`daily-nav-firm-version-current-${version.id}`}
                        />
                      )}
                    </Stack>
                  }
                  secondary={version.created_by_name ? `Changed by ${version.created_by_name}` : undefined}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      ) : (
        <Text variant="body2">No configuration changes have been saved yet.</Text>
      )}
      {hasMoreHistory && (
        <Button
          fullWidth
          size="small"
          onClick={() => loadMoreHistory()}
          disabled={isLoadingMoreHistory}
          data-testid="daily-nav-firm-history-load-more">
          {isLoadingMoreHistory ? 'Loading...' : 'Load More'}
        </Button>
      )}
      {selectedVersion && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Text variant="body2" fontWeight="bold" gutterBottom>
            Values in effect {formatDailyNavEffectivePeriod(selectedVersion)}
          </Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary">
            TRADING DAY WINDOWS
          </Text>
          {DAILY_NAV_WINDOW_SESSIONS_FIELDS.map(field => (
            <Text key={field.key} variant="body2">
              {field.label}: {selectedVersion[field.key]}
            </Text>
          ))}
          {getDailyNavThresholdGroups(selectedVersion.short_window_sessions, selectedVersion.long_window_sessions).map(
            group => (
              <Box key={group.title} sx={{ mb: 1 }}>
                <Text variant="caption" fontWeight="bold" color="text.secondary">
                  {group.title.toUpperCase()}
                </Text>
                {group.fields.map(field => (
                  <Text key={field.key} variant="body2">
                    {field.label}: {fractionToPercent(selectedVersion[field.key]) || '—'}%
                  </Text>
                ))}
              </Box>
            )
          )}
          <Text variant="caption" fontWeight="bold" color="text.secondary">
            TRADING MARKET
          </Text>
          <Text variant="body2" sx={{ mb: 1 }}>
            {tradingMarketLabel(selectedVersion.trading_calendar)}
          </Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary">
            {DAILY_NAV_DATA_CUTOFF_LABEL.toUpperCase()}
          </Text>
          <Text variant="body2" sx={{ mb: 1 }}>
            {dailyNavDataCutoffLabel(selectedVersion.data_cutoff_time, selectedVersion.trading_calendar)}
          </Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary">
            MONITORED BY
          </Text>
          <Text variant="body2" sx={{ mb: 1 }}>
            {monitoringModeLabel(selectedVersion.monitoring_mode)}
          </Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary">
            REPORT SEND TIME
          </Text>
          <Text variant="body2" sx={{ mb: 1 }}>
            {formatDailyNavReportSendTime(selectedVersion.report_send_time)}{' '}
            {dailyNavMarketTimezone(selectedVersion.trading_calendar)}
          </Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary">
            NOTIFICATION RECIPIENTS
          </Text>
          <Text variant="body2">{recipientsLabel(selectedVersion.notification_recipients)}</Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mt: 1 }}>
            DELIVERY RECIPIENTS
          </Text>
          <Text variant="body2">{recipientsLabel(selectedVersion.delivery_recipients)}</Text>
          <Text variant="caption" fontWeight="bold" color="text.secondary" display="block" sx={{ mt: 1 }}>
            REPORT TEMPLATE
          </Text>
          <Text variant="body2">{selectedVersion.report_template_filename || 'None'}</Text>
          {(selectedVersion.report_column_map ?? []).map((mapping: DailyNavReportColumnMapping) => (
            <Text key={mapping.column_index} variant="body2" color="text.secondary">
              {mapping.column_name || '(blank header)'} →{' '}
              {mapping.kind === DailyNavReportColumnMapping.kind.LITERAL
                ? `"${mapping.value ?? ''}"`
                : (selectedVersion.report_mapping_fields?.find(field => field.key === mapping.field)?.label ??
                  mapping.field)}
            </Text>
          ))}
        </Box>
      )}
    </PrismThemeProvider>
  );
};

export default DailyNavFirmVersionHistory;
